
import { useCallback, useEffect, useState } from 'react';

type Listener<T> = (newValue: T) => void;

class StackStore<T> {
  private listeners: Listener<T>[] = [];
  private value: T[];

  constructor () {
    this.value = [];
  }

  get (): T {
    return this.value[this.value.length - 1];
  }

  push (newValue: T) {
    this.value.push(newValue);
    this.listeners.forEach(listener => listener(newValue));
  }

  pop (value: T) {
    if (this.get() === value) {
      this.value.pop();
      const newTopValue =  this.get();

      this.listeners.forEach(listener => listener(newTopValue));
    }
  }

  subscribe (callback: Listener<T>) {
    this.listeners.push(callback);
    return () => this.unsubscribe(callback);
  }

  unsubscribe (callback: Listener<T>) {
    this.listeners.filter((listener) => listener !== callback);
  }
}

const globalStores = new Map<string, StackStore<any>>(); 

function useGlobalStackState<T> (key: string): [T, (value: T) => void, (value: T) => void] {
  let store = globalStores.get(key);
  if (!store) {
    store = new StackStore<T>();
    globalStores.set(key, store);
  }

  const [state, setState] = useState<T>(store.get());

  useEffect(() => {
    return store!.subscribe(setState);
  }, []);

  const push = useCallback((value: T) => {
    store!.push(value);
  }, []);

  const pop = useCallback((value: T) => {
    store!.pop(value);
  }, []);

  return [state, push, pop];
}

const GlobalState = {
  useGlobalStackState
};

export {
  useGlobalStackState
};

export default GlobalState;